import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useExpandableText } from '@skytech/utils'
import { sanitizeProductDescription } from '@skytech/utils/html-sanitization'
import ImageOffIcon from 'mdi-react/ImageOffIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import type { Product } from '@/modules/ecom-policy/types'

import './single-product-group-overview.scss'

const SingleProductGroupOverview: FC<{
  description: string | null
  groupName: string
  groupedProducts: Product[]
  currency: string | null
  isSingleProductSelected: boolean
  onModalClose: () => void
  onProductSelect: () => void
}> = ({
  description,
  groupName,
  currency,
  groupedProducts,
  isSingleProductSelected,
  onModalClose,
  onProductSelect,
}) => {
  const [product] = groupedProducts
  const { imgUrl: imageUrl, price } = product

  const { textRef, isTextExpandable, isFullTextVisible, onFullTextVisibleSet } =
    useExpandableText(120)

  const handleSelectButtonClick = () => {
    onProductSelect()
    onModalClose()
  }

  return (
    <div className="single-product">
      <header>
        <h3> {groupName}</h3>
      </header>

      <div className="img">
        <ImageWithIconFallback
          src={imageUrl ?? ''}
          alt="product"
          fallbackIcon={<ImageOffIcon className="missing-image-placeholder" />}
        />
      </div>

      {description && description.length >= 10 && (
        <div
          ref={textRef}
          className={twJoin(
            'description',
            isFullTextVisible && 'is-full-desc-visible',
            isTextExpandable && 'is-desc-expandable'
          )}
        >
          <div dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(description) }} />
        </div>
      )}

      <div className={twJoin('desc-toggle-wrapper', isTextExpandable && 'is-desc-expandable')}>
        <button onClick={onFullTextVisibleSet}>
          <Translation
            id={isFullTextVisible ? 'Les mindre...' : 'Les mer...'}
            className="lower-case"
          />
        </button>
      </div>

      <div className="price">
        <FormattedNumberWithCurrency amount={price} currency={currency} />
      </div>

      <footer>
        <Button variant="tertiary" onClick={onModalClose}>
          <Translation id="Cancel" />
        </Button>

        <Button onClick={handleSelectButtonClick}>
          <Translation id={isSingleProductSelected ? 'Remove' : 'Add'} />
        </Button>
      </footer>
    </div>
  )
}

export default SingleProductGroupOverview
